import React, { useState, useEffect } from 'react'
import _ from 'lodash'
import { useSelector, useDispatch } from 'react-redux'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { colors, breakpoints } from 'globalStyles'
import { OutlineButton } from 'reusableComponents/Buttons/Buttons'
import { ReactComponent as PurolatorStore } from 'assets/svg/Purolator-store-lg.svg'
import { ReactComponent as PenToSquare } from 'assets/svg/pen-to-square-fa.svg'
import { ReactComponent as HouseIcon } from 'assets/svg/house-building-fa.svg'
import { ReactComponent as MessageLine } from 'assets/svg/Message-lg.svg'
import { ReactComponent as StoredIcon } from 'assets/svg/circle-check-filled.svg'
import SVGIcon from 'reusableComponents/SVGIcon'
import Verification from 'components/Verification'
import { getDOMEncodeString } from 'utils/helperFunctions'
import SetPreferencesPopUp from 'components/Preferences/SetPreferencesPopUp'
import {
  selectIsUserVerified,
  selectDeliverySummary,
  selectAddressCorrectionInfo,
  selectAllowFlagsForPreferences,
} from 'store/shipping/shippingSelectors'
import constants from 'constants.json'
import { selectVirtualAgentUrl } from 'store/global/globalSelectors'
import { setShipmentPreferences } from 'store/shipping/shippingSlice'
import SignaturePreference from 'components/SignaturePreference'
import DropOffLocationPreference from 'components/DropOffLocationPreference'

const StyledDiv = styled.div`
  text-align: center !important;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: ${colors.secondary_grey};
  @media only screen and ${breakpoints.tablet} {
    font-weight: 400;
  }
  @media only screen and ${breakpoints.desktop} {
    text-align: left !important;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
  }
`

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  .btn-outline {
    @media only screen and ${breakpoints.tablet} {
      padding: 16px !important;
    }
    @media only screen and ${breakpoints.desktop} {
      padding: 20px 24px !important;
    }
  }

  @media only screen and ${breakpoints.desktop} {
    flex-direction: row;
    width: 100%;
    gap: 24px;
  }
`

const SetAndViewPreferences = () => {
  const { t } = useTranslation()
  const [showPopUp, setShowPopUp] = useState(false)
  const [popUpType, setPopUpType] = useState('dropOffLocation')
  const [showSignPreferencePopUp, setShowSignPreferencePopUp] = useState(false)
  const [showLocPreferencePopUp, setShowLocPreferencePopUp] = useState(false)
  const [signPopUpType, setSignPopUpType] = useState('signAuth')
  const [showVerificationPopUp, setShowVerificationPopUp] = useState(false)
  const [pendingAction, setPendingAction] = useState(() =>
    sessionStorage.getItem('pendingPreferenceAction')
  )
  const { shipmentPin, requestedAddressCorrection } = useSelector(selectAddressCorrectionInfo)
  const { allowHFPUChange, hasHFPURequested, hasHFPUCompleted } = useSelector(
    selectAllowFlagsForPreferences
  )

  const isVerifiedUser = useSelector(selectIsUserVerified)
  const virtualAgentUrl = useSelector(selectVirtualAgentUrl)

  const addressCorrectionStatus = _.get(requestedAddressCorrection, 'addressCorrectionStatus', null)

  const ADDRESS_CORRECTION_IN_PROGRESS = constants.ADDRESS_CORRECTION.IN_PROGRESS
  const hfpuURL = `${virtualAgentUrl}&intent=Shipment.Hold&pin=${shipmentPin}`

  const {
    deliveryLocation,
    // buzzerCode,
    additionalDeliveryInstructions,
    hideAdditionalInstructions,
    isSignatureRequiredAndNotWaived,
    hideDropOffLocation,
    hideWaiveSignature,
    remoteSignaturePreference: remoteSignPref,
  } = useSelector(selectDeliverySummary)

  const dispatch = useDispatch()

  const clearSessionStorage = () => {
    setPendingAction(null)
    sessionStorage.removeItem('pendingPreferenceAction')
  }

  const performAction = (popupType) => {
    if (popupType === 'waiveSignature') {
      setShowSignPreferencePopUp(true)
      setSignPopUpType('signAuth')
    } else if (popupType === 'hfpu') {
      window.open(hfpuURL, '_blank')
      setPopUpType(popupType)
      clearSessionStorage()
    } else if (popupType === 'dropOffLocation') {
      setShowLocPreferencePopUp(true)
    } else {
      setShowSignPreferencePopUp(false)
      setShowPopUp(true)
      setPopUpType(popupType)
    }
  }

  useEffect(() => {
    if (!pendingAction) return
    if (isVerifiedUser) {
      performAction(pendingAction)
    } else {
      setShowVerificationPopUp(true)
    }
  }, [isVerifiedUser, pendingAction, performAction])

  // Placeholder visibility logic; replace with real business logic
  const showHFPUButton =
    allowHFPUChange && addressCorrectionStatus !== ADDRESS_CORRECTION_IN_PROGRESS // Delivery to a Purolator location

  const labelDeliveryToLocation = t('set_preferences.deliver_loc')
  const labelWaiveSignature = t('set_preferences.waive_sign')
  const labelWhereToLeave = t('set_preferences.where_to_leave')
  const labelAddInstructions = t('set_preferences.add_del_instructions')

  const buttonIcon = (iconSVG) => {
    return (
      <SVGIcon
        Icon={iconSVG}
        color={colors.secondary_light_blue}
        respSize={{
          phone: { size: '18px' },
          tablet: { size: '18px' },
          desktop: { size: '24px' },
        }}
        className="icon"
      />
    )
  }

  const onClickPopUpAction = (popupType) => {
    if (isVerifiedUser) {
      performAction(popupType)
    } else {
      setPendingAction(popupType)
      // Store popupType directly in sessionStorage
      sessionStorage.setItem('pendingPreferenceAction', popupType)
      setShowVerificationPopUp(true)
    }
  }

  const onHidePopUp = () => {
    setShowPopUp(false)
    clearSessionStorage()
  }

  const onSaveDeliveryPreferences = (instr, buzzCode, loc, remoteSignaturePreference) => {
    clearSessionStorage()
    dispatch(setShipmentPreferences(instr, buzzCode, loc, remoteSignaturePreference))
  }

  if (!hasHFPURequested && !hasHFPUCompleted) {
    return (
      <>
        <StyledDiv>{t('set_preferences.personalize_del_pref')}</StyledDiv>
        <ButtonsContainer>
          {showHFPUButton && (
            <OutlineButton
              icon={buttonIcon(PurolatorStore)}
              label={labelDeliveryToLocation}
              action={() => onClickPopUpAction('hfpu')}
            />
          )}
          {!hideWaiveSignature && (
            <OutlineButton
              icon={buttonIcon(PenToSquare)}
              label={labelWaiveSignature}
              action={() => onClickPopUpAction('waiveSignature')}
              stored={remoteSignPref === 'PTL'}
              storedIcon={getDOMEncodeString(buttonIcon(StoredIcon))}
            />
          )}
          {!(hideDropOffLocation || isSignatureRequiredAndNotWaived) && (
            <OutlineButton
              icon={buttonIcon(HouseIcon)}
              label={labelWhereToLeave}
              action={() => onClickPopUpAction('dropOffLocation')}
              stored={!!deliveryLocation}
              storedIcon={getDOMEncodeString(buttonIcon(StoredIcon))}
            />
          )}
          {!(hideAdditionalInstructions || isSignatureRequiredAndNotWaived) && (
            <OutlineButton
              icon={buttonIcon(MessageLine)}
              label={labelAddInstructions}
              action={() => onClickPopUpAction('deliveryInstructions')}
              stored={!!additionalDeliveryInstructions}
              storedIcon={getDOMEncodeString(buttonIcon(StoredIcon))}
            />
          )}
          <Verification
            show={showVerificationPopUp}
            onHide={(closeValue) => {
              setShowVerificationPopUp(closeValue)
              // If user cancels verification, clear pending action
              if (closeValue === false) {
                clearSessionStorage()
              }
            }}
          />
        </ButtonsContainer>
        {popUpType !== 'hfpu' && (
          <SetPreferencesPopUp
            show={showPopUp}
            onHide={onHidePopUp}
            popUpType={popUpType}
            onSave={onSaveDeliveryPreferences}
          />
        )}
        <SignaturePreference
          show={showSignPreferencePopUp}
          onHide={() => {
            setShowSignPreferencePopUp(false)
            clearSessionStorage()
          }}
          popUpType={signPopUpType}
        />
        <DropOffLocationPreference
          show={showLocPreferencePopUp}
          onHide={() => {
            setShowLocPreferencePopUp(false)
            clearSessionStorage()
          }}
          popUpType="dropOffLocation"
          onSave={onSaveDeliveryPreferences}
        />
      </>
    )
  }
  return null
}

SetAndViewPreferences.propTypes = {}

export default SetAndViewPreferences
